import React from 'react';
import mtStyles from '../../styles/mtStyles';
import SmsIcon from '@material-ui/icons/Sms';
import EmailIcon from '@material-ui/icons/Email';
import PrintIcon from '@material-ui/icons/Print';
import convertIsoDatetimeToDate from '../../functions/convertIsoDatetimeToDate';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { TableCell, TableRow, Typography, Link, IconButton, Chip, Tooltip } from '@material-ui/core';
import { Box } from '@material-ui/core';
import { useSelector } from 'react-redux';

import DataTable from '../datatable';

import {
  useGetAllSMSTemplatesQuery,
  useGetAllEmailTemplatesQuery,
  useGetAllPrintTemplatesQuery,
  useGetAllCampaignsQuery,
  useGetCampaignTemplatesQuery
} from '../../apis/backend';


const CampaignList = () => {
  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);

  const skipLoadingContent = !chainId || !siteId;
  const { data: campaignList, isLoading: isLoadingCampaigns, isFetching: isFetchingCampaigns, error: errorCampaigns } = useGetAllCampaignsQuery({ chainId, siteId }, { skip: skipLoadingContent});
  const { data: campaignsTemplates, isLoading: isLoadingCampaignTemplates, error: isCampaignTemplatesError } = useGetCampaignTemplatesQuery({ chainId, siteId }, { skip: skipLoadingContent });
  const { data: smsTemplateList, isLoadingSMS, errorSMS } = useGetAllSMSTemplatesQuery({ chainId, siteId }, { skip: skipLoadingContent });
  const { data: emailTemplateList, isLoadingEmail, errorEmail } = useGetAllEmailTemplatesQuery({ chainId, siteId }, { skip: skipLoadingContent });
  const { data: printTemplateList, isLoadingPrint, errorPrint } = useGetAllPrintTemplatesQuery({ chainId, siteId }, { skip: skipLoadingContent });

  const history = useHistory();
  const { t } = useTranslation();

  const classes = mtStyles();

  const isLoading = isLoadingCampaigns || isFetchingCampaigns || isLoadingCampaignTemplates || isLoadingSMS || isLoadingEmail || isLoadingPrint;
  const error = errorCampaigns || isCampaignTemplatesError || errorSMS || errorEmail || errorPrint;

  if (error) console.error(JSON.stringify(error.data));

  const campaignHasTemplate = (campaign, entityType) => {
    if (!campaignsTemplates?.length) return;
    const campaignSites = campaign.sites.map(s => s.siteId);
    let defaultTemplate, templateMatched;
    switch (entityType) {
    case 'sms':
      if (!smsTemplateList?.length) return;
      defaultTemplate = smsTemplateList.find(t => campaignSites.includes(t.site_id) && t.ongoing);
      templateMatched = campaignsTemplates.find(i => i.campaign_id === campaign.id && i.sms_template_id);
      break;
    case 'email':
      if (!emailTemplateList?.length) return;
      defaultTemplate = emailTemplateList.find(t => campaignSites.includes(t.site_id) && t.ongoing);
      templateMatched = campaignsTemplates.find(i => i.campaign_id === campaign.id && i.email_template_id);
      break;
    case 'print':
      if (!printTemplateList?.length) return;
      defaultTemplate = printTemplateList.find(t => campaignSites.includes(t.site_id) && t.ongoing);
      templateMatched = campaignsTemplates.find(i => i.campaign_id === campaign.id && i.print_template_id);
      break;
    }
    return templateMatched || defaultTemplate ? true : false;
  };

  const viewCampaign = (id) => {
    history.push(`/campaigns/${id}`);
  };

  const Row = (props) => {
    const { campaign, index} = props;
    const vehicleClasses = campaign.vehicleClasses ? campaign.vehicleClasses.join(', ') : '-';
    const campaignSiteCount = campaign.sites ? campaign.sites.length : '-';
    const sites = campaign.sites.map(s => <Typography key={'site-' + s.siteId}>{s.name} ({s.siteId})</Typography>);

    const smsIconColor = campaignHasTemplate(campaign, 'sms') ? classes.activeIcon : classes.disabledIcon;
    const emailIconColor = campaignHasTemplate(campaign, 'email') ? classes.activeIcon : classes.disabledIcon;
    const printIconColor = campaignHasTemplate(campaign, 'print') ? classes.activeIcon : classes.disabledIcon;

    const assignedTemplate = (() => {
      const campaignTemplates = campaignsTemplates
        ? campaignsTemplates.find(t => t.campaign_id == campaign.id)
        : '';
      let templateId;
      if (campaignTemplates && campaignTemplates.sms_template_id) {
        templateId = campaignTemplates.sms_template_id;
        return smsTemplateList?.find(t => t.id === templateId);
      } else {
        const campaignSites = campaign.sites.map(s => s.siteId);
        return smsTemplateList?.find(t => campaignSites.includes(t.site_id) && t.ongoing);
      }
    })();

    return (
      <TableRow
        style={index % 2 ? { background: '#FBFBFB' } : { background: 'white' }}
        key={index}>
        <TableCell>
          <Typography noWrap>{campaign.id}</Typography>
        </TableCell>
        <TableCell>
          <Link
            className={classes.link}
            onClick={() => viewCampaign(campaign.id)}>
            <Typography noWrap>{campaign.name}</Typography>
          </Link>
        </TableCell>
        <TableCell>
          <Typography noWrap>{campaign.code.toUpperCase()}</Typography>
        </TableCell>
        <TableCell>
          <Tooltip title={sites} arrow>
            <Chip className={classes.chipSiteCount} label={campaignSiteCount} />
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography noWrap>{vehicleClasses}</Typography>
        </TableCell>
        <TableCell>
          <Typography noWrap>{convertIsoDatetimeToDate(campaign.valid.start)}</Typography>
        </TableCell>
        <TableCell>
          <Typography noWrap>{convertIsoDatetimeToDate(campaign.valid.end)}</Typography>
        </TableCell>
        <TableCell>
          <Typography noWrap>{campaign.price?.priceText}</Typography>
        </TableCell>
        <TableCell>
          <Box className={classes.iconsRowContainer}>
            <Tooltip title={assignedTemplate ? t('sms-template-active', { template: assignedTemplate }) : ''} arrow>
              <IconButton><SmsIcon className={smsIconColor} /></IconButton>
            </Tooltip>
            <Tooltip title={assignedTemplate ? t('sms-template-active', { template: assignedTemplate }) : ''} arrow>
              <IconButton><EmailIcon className={emailIconColor} /></IconButton>
            </Tooltip>
            <Tooltip title={assignedTemplate ? t('sms-template-active', { template: assignedTemplate }) : ''} arrow>
              <IconButton><PrintIcon className={printIconColor} /></IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>);
  };

  const mapTableBody = (campaigns) => {
    return campaigns.map((campaign, index) => <Row campaign={campaign} key={index} index={index} />);
  };

  const columns = [
    {
      Header: 'Id',
      accessor: 'id'
    },
    {
      Header: 'Name',
      accessor: 'title'
    },
    {
      Header: 'Code',
      accessor: 'code'
    },
    {
      Header: 'Stations',
      accessor: 'sites'
    },
    {
      Header: 'Vehicle Types',
      accessor: 'vehicleClasses',
    },
    {
      Header: 'Start',
      accessor: 'start',
    },
    {
      Header: 'End',
      accessor: 'valid.end',
    },
    {
      Header: 'Price',
      accessor: 'price',
    },
    {
      Header : 'Channels',
    }
  ];

  return (
    <>
      <DataTable
        columns={columns}
        mapTableBody={mapTableBody}
        data={!isFetchingCampaigns && campaignList?.campaigns && siteId ? campaignList.campaigns : []}
        isLoading={isLoading} />
    </>
  );
};


export default CampaignList;
